/**
 * Manual issue filing — the "Link issue" action on a failed run assertion
 * (`StepAssertions`). Two operations: file a brand-new issue, or attach a new
 * occurrence to an issue that's already open for the workflow. Both accept
 * `subjectAssertionKey` + `runId` to link the issue to that specific failing
 * assertion result (see `WorkflowAssertionResult.issueId`).
 *
 * @module hooks/useManualIssueMutations
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateManualIssuePayload,
  AddIssueOccurrencePayload,
  IssueDetailResponse,
} from '@chatsdet/types/issues';
import { API_URL } from '../config.js';
import { toApiError } from '../lib/api-error';

type ManualIssueResponse = {
  ok: boolean;
  data: IssueDetailResponse;
};

/** Files a brand-new issue (optionally linked to a failing run assertion). */
export function useCreateManualIssueMutation(workflowId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateManualIssuePayload) => {
      const response = await fetch(`${API_URL}/v2/issues`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to create issue');
      }

      const body = (await response.json()) as ManualIssueResponse;
      if (!body?.ok) {
        throw new Error('Failed to create issue');
      }
      return body.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-open-issues', workflowId] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['issues-v2'] }).catch(() => {});
    },
  });
}

/** Attaches a new occurrence to an already-open issue. */
export function useAddIssueOccurrenceMutation(
  workflowId: string | null | undefined,
  issueId: string | null
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: AddIssueOccurrencePayload) => {
      if (!issueId) throw new Error('Issue ID is required');

      const response = await fetch(`${API_URL}/v2/issues/${issueId}/occurrences`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to attach issue occurrence');
      }

      const body = (await response.json()) as ManualIssueResponse;
      if (!body?.ok) {
        throw new Error('Failed to attach issue occurrence');
      }
      return body.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-open-issues', workflowId] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['issue-detail-v2', issueId] }).catch(() => {});
    },
  });
}
